import { flushSync } from 'react-dom';

/** Вкладки ещё считают видимость для нового режима (ставит DocumentTabBuilder). */
export const DOCUMENT_TABS_SETTLING_CLASS = 'document-tabs--settling';

/** Класс на <html> на время смены режима: им гасятся собственные анимации частей карточки. */
const ACTIVE_CLASS = 'document-mode-transition';

/** Тишина в DOM формы, после которой считаем, что форма доехала до нового режима. */
const QUIET_MS = 60;
/** Дольше старый кадр не держим: Chrome сам обрывает переход через 4 с. */
const SETTLE_BUDGET_MS = 1500;

const PANEL_ENTERED_ATTR = 'data-document-mode-entered';

const BLOCK_SELECTOR = '.box-container[data-formula-trace-target-id]';

/** Части карточки, которые едут сами по себе, а не вместе с корневым снимком. */
const PARTS: readonly [selector: string, name: string][] = [
    ['.doc-header-actions', 'document-actions'],
    ['.document-tabs > .rf-tabs__mantine > .rf-tabs__navigation', 'document-tabs'],
    ['.document-workspace__navigator-rail', 'document-panel'],
    ['.document-workspace-layout > .document-tool-rail', 'document-rail'],
];

/**
 * Рабочая область карточки. Склейку зовут и изнутри (тулбар), и снаружи (форма карточки
 * оборачивает раскладку целиком), поэтому ищем и вверх, и вниз. Раскладка с инструментами
 * шире листа: в неё входят панель и рейл.
 */
function findScope(origin: Element): Element | null {
    for (const selector of ['.document-workspace-layout', '.document-workspace']) {
        const scope = origin.closest(selector) ?? origin.querySelector(selector);
        if (scope) return scope;
    }
    return null;
}

export const isDocumentModeTransitionActive = () => document.documentElement.classList.contains(ACTIVE_CLASS);

function activeScroller(scope: Element): HTMLElement | null {
    return scope.querySelector<HTMLElement>('.document-tabs [role="tabpanel"]:not([aria-hidden]) .doc-tab');
}

/**
 * Верхние блоки активной вкладки, чей заголовок виден в листе. Блок, наполовину ушедший под
 * шапку, сюда не попадает: снимок перехода лист не подрезает, и такой блок просвечивал бы
 * поверх шапки. Он остаётся в корневом снимке и меняется склейкой.
 */
function visibleBlocks(scope: Element): HTMLElement[] {
    const scroller = activeScroller(scope);
    if (!scroller) return [];
    const view = scroller.getBoundingClientRect();
    return Array.from(scroller.querySelectorAll<HTMLElement>(BLOCK_SELECTOR)).filter((block) => {
        if (block.parentElement?.closest(BLOCK_SELECTOR)) return false;
        const rect = block.getBoundingClientRect();
        return rect.height > 0 && rect.top >= view.top - 1 && rect.top < view.bottom;
    });
}

const blockId = (block: HTMLElement) => block.dataset.formulaTraceTargetId ?? '';

/** Последний запущенный переход: только он снимает имена и класс, пропущенные — нет. */
let latestTransition = 0;
let namedElements: HTMLElement[] = [];

function clearNames(): void {
    for (const element of namedElements) {
        element.style.removeProperty('view-transition-name');
        element.style.removeProperty('view-transition-class');
    }
    namedElements = [];
}

/** Имена ставятся только видимым частям: каждая названная часть — отдельный снимок. */
function nameParts(scope: Element, blocks: readonly HTMLElement[]): void {
    clearNames();
    for (const [selector, name] of PARTS) {
        const element = scope.querySelector<HTMLElement>(selector);
        if (!element) continue;
        element.style.setProperty('view-transition-name', name);
        namedElements.push(element);
    }
    const used = new Set<string>();
    for (const block of blocks) {
        const name = `document-block-${blockId(block).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
        if (used.has(name)) continue;
        used.add(name);
        block.style.setProperty('view-transition-name', name);
        block.style.setProperty('view-transition-class', 'document-block');
        namedElements.push(block);
    }
}

interface IScrollAnchor {
    id: string;
    top: number;
    atStart: boolean;
}

/**
 * Якорь — первый блок, чей заголовок виден, и его место на экране: шапка меняет высоту, а блок
 * остаётся у глаз. Блок, наполовину ушедший под шапку, в якоря не годится: в правке он растёт
 * и сталкивал бы вниз всё, на что пользователь смотрит.
 */
function captureAnchor(scope: Element, blocks: readonly HTMLElement[]): IScrollAnchor | undefined {
    const scroller = activeScroller(scope);
    const block = blocks[0];
    if (!scroller || !block) return undefined;
    return { id: blockId(block), top: block.getBoundingClientRect().top, atStart: scroller.scrollTop === 0 };
}

function restoreAnchor(scope: Element, anchor: IScrollAnchor | undefined): void {
    const scroller = activeScroller(scope);
    if (!anchor || !scroller) return;
    if (anchor.atStart) {
        scroller.scrollTop = 0;
        return;
    }
    const block = scroller.querySelector(`[data-formula-trace-target-id="${CSS.escape(anchor.id)}"]`);
    if (block) scroller.scrollTop += block.getBoundingClientRect().top - anchor.top;
}

/**
 * Ждёт, пока форма доедет до нового режима: вкладки досчитали видимость, и DOM затих.
 * Пока ждём, браузер держит старый кадр — промежуточные коммиты на экран не попадают.
 */
function waitForSettle(scope: Element): Promise<void> {
    return new Promise((resolve) => {
        let quiet = 0;
        const finish = () => {
            observer.disconnect();
            window.clearTimeout(quiet);
            window.clearTimeout(budget);
            resolve();
        };
        const check = () => {
            if (scope.querySelector(`.${DOCUMENT_TABS_SETTLING_CLASS}`)) arm();
            else finish();
        };
        const arm = () => {
            window.clearTimeout(quiet);
            quiet = window.setTimeout(check, QUIET_MS);
        };
        const observer = new MutationObserver(arm);
        observer.observe(scope, { childList: true, subtree: true, characterData: true });
        const budget = window.setTimeout(finish, SETTLE_BUDGET_MS);
        arm();
    });
}

/**
 * Смена режима карточки (просмотр ⇄ правка) одной склейкой.
 *
 * Режим перестраивает карточку несколькими коммитами: поля, панель «Заполнение», набор
 * вкладок (видимость считается асинхронно), содержимое вкладки. Без склейки каждый коммит
 * рисуется отдельным кадром со своей геометрией. View Transition держит старый кадр, пока
 * форма не доедет до нового режима, а затем ведёт видимые блоки, шапку и панель со старых
 * мест на новые. При reduced motion анимации гасит CSS — остаётся мгновенная склейка.
 */
export async function runDocumentModeTransition(origin: Element | null, update: () => void): Promise<void> {
    const scope = origin && findScope(origin);
    if (!scope || typeof document.startViewTransition !== 'function' || document.visibilityState !== 'visible') {
        update();
        return;
    }

    const root = document.documentElement;
    const token = ++latestTransition;
    const blocks = visibleBlocks(scope);
    const anchor = captureAnchor(scope, blocks);
    nameParts(scope, blocks);
    root.classList.add(ACTIVE_CLASS);

    const transition = document.startViewTransition(async () => {
        clearNames();
        flushSync(update);
        await waitForSettle(scope);
        // Пока ждали, начался следующий переход: имена и прокрутка теперь его.
        if (token !== latestTransition) return;
        // Панель, открытую склейкой, ведёт переход. Её собственный вход гасится атрибутом на
        // узле, а не только классом на <html>: снятие класса вернуло бы анимацию, и браузер
        // проиграл бы вход ещё раз.
        for (const panel of scope.querySelectorAll('.document-tool-panel')) panel.setAttribute(PANEL_ENTERED_ATTR, '');
        restoreAnchor(scope, anchor);
        nameParts(scope, visibleBlocks(scope));
    });
    // Переход пропускается (вкладка ушла в фон, следующий переход) — DOM к этому моменту уже
    // обновлён, остаётся снять имена.
    transition.ready.catch(() => undefined);
    transition.finished
        .catch(() => undefined)
        .finally(() => {
            if (token !== latestTransition) return;
            clearNames();
            root.classList.remove(ACTIVE_CLASS);
        });
    // Пропуск перехода updateCallbackDone не отклоняет — отклонит только ошибка самой смены режима.
    await transition.updateCallbackDone;
}
